






















































import { PropType } from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import {
  createCampaign,
  getCampaignDetails,
  updateCampaign
} from '@/api/CopyStake/GamificationCenter/Jackpot';
import { statusMap } from '@/views/CopyStake/CopyStakeGamificationCenter/Jackpot/CampaignsTable/CopyStakeJackpotCampaignsTable.vue';
import {
  EJackpotCampaignStatusType,
  IJackpotCampaignDetails
} from '@/api/schema';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { AxiosError } from 'axios';
import { fromPercents, toPercents } from '@/helpers/decimalsHelpers';

const VStatus = (): Promise<any> => import('@/components/VStatus.vue');
const CopyStakeJackpotCampaignDialogMainForm = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeGamificationCenter/Jackpot/ManageCampaignDialog/CopyStakeJackpotCampaignDialogMainForm.vue'
  );

const defaultCampaign = {
  name: null,
  minViewersThreshold: null,
  startDrawThresholdMinutes: null,
  jackpotBetPercentage: null,
  jackpotExpected: null,
  startAt: null,
  finishAt: null,
  streamer: null
};

const defaultServerErrors = {
  name: null
};

export default {
  name: 'CopyStakeJackpotCampaignDialog',
  components: {
    BaseDialog,
    VStatus,
    CopyStakeJackpotCampaignDialogMainForm
  },
  props: {
    value: {
      type: Boolean,
      default: (): boolean => false
    },
    campaignId: {
      type: Number as PropType<number> | null,
      default: null
    },
    isCopyMode: {
      type: Boolean,
      default: (): boolean => false
    }
  },
  data(): any {
    return {
      EJackpotCampaignStatusType,
      statusMap,

      formData: { ...defaultCampaign },
      editableCampaign: null,

      serverErrors: { ...defaultServerErrors },
      isLoading: false,
      isFormsValid: false
    };
  },
  computed: {
    showDialog: {
      get(): boolean {
        return this.value;
      },
      set(value: boolean): void {
        this.$emit('input', value);
      }
    },
    dialogTitle(): string {
      if (this.isViewMode) {
        return 'Campaign Details';
      } else if (this.isEditMode) {
        return 'Edit Campaign';
      }

      return 'Add Campaign';
    },
    isEditMode(): boolean {
      return !!this.campaignId && !this.isCopyMode;
    },
    isViewMode(): boolean {
      return (
        this.isEditMode &&
        this.editableCampaign?.status !== EJackpotCampaignStatusType.PENDING
      );
    },
    isShowSubmitBtn(): boolean {
      return !this.isViewMode;
    },
    isDisabledSubmitBtn(): boolean {
      return !this.isFormsValid || this.isLoading;
    }
  },
  watch: {
    async campaignId(id: number): Promise<void> {
      if (id) {
        await this.setCampaignDetails();
      }
    }
  },
  methods: {
    async setCampaignDetails(): Promise<void> {
      if (!this.campaignId) return;

      try {
        this.isLoading = true;

        const campaignDetailsData = await getCampaignDetails(this.campaignId);

        this.editableCampaign = { ...campaignDetailsData };

        this.formData = {
          name: this.isCopyMode
            ? campaignDetailsData.name + ' (Copied)'
            : campaignDetailsData.name,
          minViewersThreshold: campaignDetailsData.minViewersThreshold,
          startDrawThresholdMinutes:
            campaignDetailsData.startDrawThresholdMinutes,
          jackpotBetPercentage: campaignDetailsData.jackpotBetPercentage
            ? toPercents(campaignDetailsData.jackpotBetPercentage)
            : null,
          jackpotExpected: campaignDetailsData.jackpotExpected,
          startAt: campaignDetailsData.startAt,
          finishAt: campaignDetailsData.finishAt,
          streamer: { ...campaignDetailsData.streamer }
        };
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    updateCampaignDetailsHandler(campaign: IJackpotCampaignDetails): void {
      this.formData = { ...this.formData, ...campaign };
    },
    async handlerClickSubmitButton(): Promise<void> {
      try {
        if (!this.isFormsValid) return;

        this.isLoading = true;

        const campaignData = {
          ...this.formData,
          jackpotBetPercentage: this.formData.jackpotBetPercentage
            ? fromPercents(this.formData.jackpotBetPercentage)
            : null
        };

        this.isEditMode
          ? await updateCampaign(this.campaignId, campaignData)
          : await createCampaign(campaignData);

        this.$toast.success(
          `Campaign has been successfully ${
            this.isEditMode ? 'updated' : 'created'
          }.`
        );

        this.isLoading = false;
        this.closeDialog();
        this.$emit('submit');
      } catch (error) {
        this.parseServerErrors(error);
        errorToastMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    async handlerClickCloseBtn(): Promise<void> {
      this.closeDialog();
    },
    parseServerErrors(error: AxiosError): void {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message;

        if (!errorMessage) return;

        if (errorMessage.includes('] already exists')) {
          this.serverErrors.name = errorMessage;
        }
      }
    },
    resetForms(): void {
      this.resetServerErrors();

      this.isFormsValid = false;
      this.editableCampaign = null;
      this.formData = {
        ...defaultCampaign
      };
    },
    resetServerErrors(key?: string): void {
      if (key && this.serverErrors[key]) {
        this.serverErrors[key] = null;
      } else {
        this.serverErrors = { ...defaultServerErrors };
      }
    },
    closeDialog(): void {
      if (this.isLoading) return;

      this.resetForms();

      this.$emit('close');
      this.showDialog = false;
    }
  }
};
